'use client';

import type { CSSProperties, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import ThemeToggle from '@/components/ThemeToggle';
import {
  AuthPageBrandingProvider,
  useResolvedAuthPageBranding,
} from '@/hooks/useAuthPageBranding';
import type { AuthPageBrandingSnapshot } from '@/lib/authPageBrandingShared';

interface AuthShellProps {
  children: ReactNode;
  initialBranding: AuthPageBrandingSnapshot;
  initialDesktopShell: boolean;
}

const AUTH_HERO_IMAGE_VAR = '--auth-hero-image' as const;

function AuthShellInner({ children }: Pick<AuthShellProps, 'children'>) {
  const pathname = usePathname();
  const authPage = pathname?.startsWith('/diagnostics')
    ? 'diagnostics'
    : pathname === '/auth/new-client-signup'
      ? 'new-client-signup'
      : pathname === '/auth/signup'
        ? 'signup'
        : 'default';
  const {
    isDesktopShell,
    isNativePlatform,
    isTauriDesktop,
    authBackgroundUrl,
    authLayout,
    websiteHref,
    websiteLogoUrl,
    websiteLogoAlt,
  } = useResolvedAuthPageBranding();

  const backdropStyle = authBackgroundUrl
    ? ({ [AUTH_HERO_IMAGE_VAR]: `url(${authBackgroundUrl})` } as CSSProperties)
    : undefined;

  return (
    <div
      className={`auth-shell min-h-screen flex flex-col ${authBackgroundUrl ? 'auth-shell--has-background' : ''}`}
      style={backdropStyle}
      data-auth-desktop-shell={isDesktopShell ? 'true' : 'false'}
      data-auth-layout={authLayout}
      data-auth-page={authPage}
    >
      {!isNativePlatform && (
        <>
          {isDesktopShell && !isTauriDesktop && (
            <>
              <div
                className={`auth-shell__backdrop ${authBackgroundUrl ? 'auth-shell__backdrop--image' : ''}`}
                aria-hidden="true"
              />
              <div className="auth-shell__backdrop-overlay" aria-hidden="true" />
            </>
          )}
          {isDesktopShell && !isTauriDesktop && (
            <div className="auth-shell__panel-controls">
              <div className="auth-shell__panel-controls-inner">
                <div className="auth-shell__theme-toggle" aria-label="Toggle theme">
                  <ThemeToggle />
                </div>
              </div>
            </div>
          )}
          {websiteHref && (
            <a
              href={websiteHref}
              className="auth-shell__website-link"
              aria-label="Back to website"
            >
              <span className="auth-shell__website-link-arrow" aria-hidden="true">
                ←
              </span>
              {websiteLogoUrl ? (
                <img
                  src={websiteLogoUrl}
                  alt={websiteLogoAlt}
                  width={112}
                  height={32}
                  loading="eager"
                  className="auth-shell__website-link-logo"
                />
              ) : (
                <span className="auth-shell__website-link-text">Website</span>
              )}
              <span className="auth-shell__website-link-label">Website</span>
            </a>
          )}
        </>
      )}
      <div className="auth-shell__page-slot relative z-[1] flex min-h-screen flex-col">
        {children}
      </div>
    </div>
  );
}

export default function AuthShell({ children, initialBranding, initialDesktopShell }: AuthShellProps) {
  return (
    <AuthPageBrandingProvider initialBranding={initialBranding} initialDesktopShell={initialDesktopShell}>
      <AuthShellInner>{children}</AuthShellInner>
    </AuthPageBrandingProvider>
  );
}
